import React, { useRef, useState } from "react";

import { submitEnquiry } from "../lib/enquiryApi";

const renderContactIcon = (type) => {
  if (type === "address") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 21s-6-4.35-6-10a6 6 0 1112 0c0 5.65-6 10-6 10z" />
        <circle cx="12" cy="11" r="2.2" />
      </svg>
    );
  }

  if (type === "phone") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.5 4.5h3l1.5 4-2 1.5a15 15 0 006 6l1.5-2 4 1.5v3A2 2 0 0118.5 20C11 20 4 13 4 5.5a2 2 0 012.5-1z" />
      </svg>
    );
  }

  if (type === "website") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18" />
        <path d="M12 3a14 14 0 010 18" />
        <path d="M12 3a14 14 0 000 18" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
};

function Contact({ content }) {
  const formRef = useRef(null);
  const [submitState, setSubmitState] = useState({
    status: "idle",
    message: "",
  });
  const whatsappUrl = `https://wa.me/${content?.whatsappNumber || ""}?text=${encodeURIComponent(
    content?.whatsappMessage || ""
  )}`;
  const mapsUrl = content?.mapsUrl || "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const payload = {
      fullName: formData.get("fullName")?.toString().trim() || "",
      company: formData.get("company")?.toString().trim() || "",
      email: formData.get("email")?.toString().trim() || "",
      phone: formData.get("phone")?.toString().trim() || "",
      interest: formData.get("interest")?.toString().trim() || "",
      message: formData.get("message")?.toString().trim() || "",
    };

    setSubmitState({
      status: "submitting",
      message: "Sending your enquiry...",
    });

    try {
      await submitEnquiry(payload);

      formRef.current?.reset();
      setSubmitState({
        status: "success",
        message:
          "Enquiry sent successfully. Our team will get back to you soon.",
      });
    } catch (error) {
      console.error("Enquiry submit failed:", error);

      setSubmitState({
        status: "error",
        message: error.message || "Unable to send the enquiry right now.",
      });
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="section-wrap">
        <div className="contact-grid">
          <div className="contact-left">
            <div className="section-eyebrow">{content?.eyebrow}</div>
            <h2 className="section-title">{content?.title}</h2>
            <p>{content?.description}</p>
            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true">
                  {renderContactIcon("address")}
                </div>
                <div>
                  <div className="contact-text-lbl">{content?.addressLabel}</div>
                  <div className="contact-text-val">
                    {(content?.addressLines || []).map((line) => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true">
                  {renderContactIcon("phone")}
                </div>
                <div>
                  <div className="contact-text-lbl">{content?.phoneLabel}</div>
                  <div className="contact-text-val">{content?.phoneValue}</div>
                  <a
                    href={whatsappUrl}
                    className="contact-whatsapp-link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Chat on WhatsApp
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true">
                  {renderContactIcon("website")}
                </div>
                <div>
                  <div className="contact-text-lbl">{content?.websiteLabel}</div>
                  <div className="contact-text-val">{content?.websiteValue}</div>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon" aria-hidden="true">
                  {renderContactIcon("hours")}
                </div>
                <div>
                  <div className="contact-text-lbl">{content?.hoursLabel}</div>
                  <div className="contact-text-val">{content?.hoursValue}</div>
                </div>
              </div>
            </div>

            <div className="contact-map-card">
              <div className="contact-map-head">
                <div>
                  <div className="contact-text-lbl">{content?.mapLabel}</div>
                  <div className="contact-map-title">{content?.mapTitle}</div>
                </div>
              </div>
              <div className="contact-map-frame">
                <iframe
                  title="Anand Technologies location map"
                  src={mapsUrl}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>

          <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
            <div className="form-title">Send an Enquiry</div>
            <input
              type="hidden"
              name="subject"
              value="New enquiry from Anand Technologies website"
            />
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="contact-name">Full Name</label>
                <input
                  id="contact-name"
                  name="fullName"
                  type="text"
                  placeholder="Your name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-company">Company / Organisation</label>
                <input
                  id="contact-company"
                  name="company"
                  type="text"
                  placeholder="Company name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-email">Email Address</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  placeholder="email@company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-phone">Phone Number</label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
              <div className="form-group full">
                <label htmlFor="contact-interest">Product Interest</label>
                <select
                  id="contact-interest"
                  name="interest"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>
                    Select a category
                  </option>
                  {(content?.interestOptions || []).map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group full">
                <label htmlFor="contact-message">
                  Message / Technical Requirements
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows="4"
                  placeholder="Describe your requirements - frequency band, quantity, connector type, application, etc."
                  required
                />
              </div>
            </div>
            <button
              className="form-submit"
              type="submit"
              disabled={submitState.status === "submitting"}
            >
              {submitState.status === "submitting"
                ? "Sending..."
                : "Send Enquiry"}
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
            {submitState.message ? (
              <p
                className={`form-status form-status-${submitState.status}`}
                role="status"
              >
                {submitState.message}
              </p>
            ) : null}
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;
